/**
 * What the cast panel and the design studio's thread say (docs/SPEC.md §6.3,
 * §6.4; P28-T14). The owner (2026-10-21) asked that every object the film
 * needs be visible and replaceable, that a missing one say what it means and
 * what to do about it, and that the studio show plainly that it is working
 * for an ad, with a way back. So every sentence names the thing, the ad or
 * the scenes, and every action says what it does.
 */
import type { CastOrigin, HeldBy, MadeFor } from "../api/cast";

/** `{key}` filled from `params`. */
export function sayCast(
  sentence: string,
  params: Record<string, string | number> = {},
): string {
  return sentence.replace(/\{(\w+)\}/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(params, key)
      ? String(params[key])
      : whole,
  );
}

/** "1", "1 and 2", "1, 2 and 4". */
export function andList(words: readonly string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

export const CAST = {
  heading: "Objects in this film",
  view: "View",
  retry: "Try again",
  viewObject: "View {name}",
  viewHelp:
    "Drag to rotate. Scroll or pinch to zoom. Shift-drag, right-drag or use two fingers to pan.",
  viewUnavailable: "This object is not ready to view yet.",
  similarTags: "Similar object tags",
  allObjects: "All objects",
  intro:
    "Everything the film shows, one row each. Replace any of them; the scenes that show it are made again.",
  /** The heading's count of rows drawn as stand-ins. */
  standinsOne: "1 object is a stand-in",
  standinsMany: "{count} objects are stand-ins",
  loading: "Reading the objects of the film…",
  none: "This film shows no objects yet.",
  origin: {
    objects: "From Objects",
    design: "Made in the design studio",
    photo: "Your photo",
    standin: "Stand-in",
    figure: "A figure from Objects",
    crowd: "Figures from Objects",
  } satisfies Record<CastOrigin, string>,
  madeFor: "made for {titles}",
  madeForThis: "this ad",
  inScene: "In scene {scenes}",
  inScenes: "In scenes {scenes}",
  inNoScene: "Not in any scene",
  /** Who holds it (P30): "Held by the woman cycling in scene 2". */
  heldBy: "Held by {people}",
  heldInScene: "{name} in scene {scenes}",
  heldInScenes: "{name} in scenes {scenes}",
  /** A missing row's flag and what can be done about it. */
  missing: "Objects has no {thing} yet. It is drawn as a stand-in.",
  missingHelp:
    "Attach a photo of the real one, make it in the design studio, or keep the simple shape.",
  attachPhoto: "Attach a photo",
  makeInStudio: "Make it in the design studio",
  useStandin: "Use the stand-in",
  kept: "You chose to keep the simple shape.",
  making: "Being made in the design studio.",
  makingHelp:
    "When it looks right, use it in this ad from the studio, and the film changes.",
  openInStudio: "Open in the design studio",
  /** The found offer. */
  foundLabel: "Found in Objects",
  found: "The {thing} is now in Objects. Redo {scenes} with it?",
  foundAnywhere: "The {thing} is now in Objects. Use it in the film?",
  foundScene: "scene {scenes}",
  foundScenes: "scenes {scenes}",
  yes: "Yes",
  notNow: "Not now",
  replace: "Replace",
  /** What a row says once its action was sent, until the film changes. */
  sent: "Change requested. Follow its progress in the chat.",
  replacementQueued: "Replacement queued.",
  replacementWorking: "Applying the replacement…",
  replacementBuilding: "Preparing the updated scenes…",
  replacementApproval: "Waiting for your approval in the chat.",
  replacementApplied: "Replacement applied.",
  replacementFailed:
    "The replacement could not be applied. Your film is unchanged.",
  retryReplacement: "Retry replacement",
  /** The picker. */
  pickerTitle: "Replace: {name}",
  tabObjects: "Objects",
  tabStudio: "Design studio",
  tabPhoto: "Photo",
  objectsHelp:
    "Choose what should take its place. Its place, its movement and its colours are kept where the new one allows.",
  search: "Search Objects",
  useThis: "Use this one",
  cancel: "Cancel",
  notAvailable: "This is not open for your organisation yet.",
  studioHelp:
    "Make a new design for it, or carry on with one of yours. When it looks right, use it in this ad from the studio, and the film changes.",
  studioNew: "Make a new design",
  studioMine: "Your designs",
  studioOpen: "Continue this one",
  studioNone: "You have no designs yet.",
  studioOwnObject:
    "This object is placed in one scene only, so a design cannot be made for it from here. Add your design to Objects, then choose it under Objects.",
  photoHelp:
    "Upload a photo of the real one. The scenes that show it are written again from your photo.",
  photoUpload: "Upload a photo",
  photoSending: "Sending…",
  /** The picker of a person and of a crowd (P29): figures only. */
  peopleHelp:
    "Choose the figure that plays them. Where they stand, what they do and the colours of their clothes are kept.",
  crowdHelp:
    "Choose the figures the crowd is drawn from: one, several, or every figure in Objects.",
  crowdEvery: "Every figure",
  crowdEveryHelp: "The crowd mixes every figure in Objects.",
  crowdChosenOne: "Drawn from 1 figure",
  crowdChosenMany: "Drawn from {count} figures",
  searchPeople: "Search people",
  useThese: "Use these",
  drawnNotPhoto: "A figure's face is drawn, never a photo.",
  /** The studio's banner. */
  banner: "Making the {thing} for “{ad}”",
  bannerHelp:
    "When it looks right, use it in the ad: it goes to Objects and takes the stand-in's place in the film.",
  bannerHelpNew:
    "Describe it and press Create. Once it looks right, you can use it in the ad.",
  backToAd: "Back to the ad",
  useIn: "Use in “{ad}”",
  using: "Adding to the film…",
  useWaits: "Build the design first, then use it in the ad.",
  /** The ad's page after Use in {ad}. */
  publishing:
    "Your design is being added to Objects. The film changes as soon as it is there.",
  used: "Your design is in the film. The scenes that show it are being made again.",
  /** Objects' card and asset page. */
  madeForObjects: "Made for {titles}",
} as const;

/** "1 object is a stand-in", "2 objects are stand-ins". */
export function standinsLine(count: number): string {
  return count === 1 ? CAST.standinsOne : sayCast(CAST.standinsMany, { count });
}

/** The row's origin in words, with "made for {ad titles}" where it was; on the page of an
 * ad it was made for, that ad is "this ad" (P28-T18: the row repeated the page's own title). */
export function originLine(
  origin: CastOrigin,
  madeFor: MadeFor[],
  adId?: string,
): string {
  const words: string = CAST.origin[origin];
  if (!madeFor.length) return words;
  const titles = madeFor.map((m) =>
    adId && m.ad_id === adId ? CAST.madeForThis : m.title,
  );
  return `${words}, ${sayCast(CAST.madeFor, { titles: andList(titles) })}`;
}

/** "In scene 2", "In scenes 1, 2 and 4". */
export function scenesLine(numbers: readonly string[]): string {
  if (!numbers.length) return CAST.inNoScene;
  return sayCast(numbers.length === 1 ? CAST.inScene : CAST.inScenes, {
    scenes: andList(numbers),
  });
}

/** The first words of a person's name that make it a description, not a
 * person's own name (fix(things-in-hand) I): "Woman on the sofa" is "the woman
 * on the sofa"; "Anna" and "Annie reading" are names and keep their capital. */
const PERSON_WORDS: ReadonlySet<string> = new Set([
  "woman",
  "women",
  "man",
  "men",
  "person",
  "people",
  "girl",
  "girls",
  "boy",
  "boys",
  "child",
  "children",
  "kid",
  "kids",
  "lady",
  "gentleman",
  "teenager",
  "teen",
  "student",
  "mother",
  "father",
  "mum",
  "mom",
  "dad",
  "grandmother",
  "grandfather",
  "grandma",
  "grandpa",
  "couple",
  "friend",
  "friends",
  "family",
  "rider",
  "cyclist",
  "runner",
  "walker",
  "worker",
  "customer",
  "chef",
  "cook",
  "driver",
  "player",
  "shopper",
  "nurse",
  "doctor",
  "barista",
  "waiter",
  "waitress",
  "presenter",
  "host",
  "guest",
  "baby",
  "toddler",
  "athlete",
  "young",
  "old",
  "older",
  "elderly",
  "senior",
  "smiling",
  "happy",
  "tired",
]);

/** A person's name as it reads after "Held by": a name that starts with an
 * article says "the " ("A woman cycling" is "the woman cycling", "An old man"
 * "the old man", "The rider" "the rider"); a name whose first word is a common
 * word for a person (`PERSON_WORDS`) says "the " and that word in lower case
 * ("Woman on the sofa" is "the woman on the sofa"); any other name, a person's
 * own ("Anna"), as it is. */
export function holderName(name: string): string {
  const text = name.trim();
  const m = /^(A|An|The) (.+)$/.exec(text);
  if (m) return `the ${m[2]}`;
  const first = text.split(/\s+/, 1)[0] ?? "";
  if (PERSON_WORDS.has(first.toLowerCase())) {
    return `the ${first.toLowerCase()}${text.slice(first.length)}`;
  }
  return text;
}

/** "Held by the woman cycling in scene 2", "Held by the woman cycling in
 * scenes 1 and 4 and the old man in scene 3" (`heldBy`'s scenes already
 * numbered); "" when nobody holds it. */
export function heldByLine(heldBy: readonly HeldBy[]): string {
  if (!heldBy.length) return "";
  const people = heldBy.map((h) =>
    sayCast(h.scenes.length === 1 ? CAST.heldInScene : CAST.heldInScenes, {
      name: holderName(h.name),
      scenes: andList(h.scenes),
    }),
  );
  return sayCast(CAST.heldBy, { people: andList(people) });
}

/** "The sofa is now in Objects. Redo scenes 2 and 3 with it?" */
export function foundLine(thing: string, numbers: readonly string[]): string {
  if (!numbers.length) return sayCast(CAST.foundAnywhere, { thing });
  const scenes = sayCast(
    numbers.length === 1 ? CAST.foundScene : CAST.foundScenes,
    { scenes: andList(numbers) },
  );
  return sayCast(CAST.found, { thing, scenes });
}

/** "Made for Spring sale and Summer launch". */
export function madeForLine(madeFor: MadeFor[]): string {
  return sayCast(CAST.madeForObjects, {
    titles: andList(madeFor.map((m) => m.title)),
  });
}
